import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';

import { AngularMaterialModule } from '../../angular-material.module';
import {
  AuthService,
  LoginBrandingConfig,
  LoginPayload,
  LoginResponse,
  UserData,
} from './auth.service';
import { AccountThemeService } from '../../services/account-theme.service';
import { TokenService } from '../../core/auth/services/token.service';
import { getDefaultLandingRoute } from '../../core/auth/role-navigation';
import {
  DEFAULT_LOGIN_UI,
  getHostName,
  LoginUiConfig,
  resolveLoginUiFromHost,
} from '../../config/login-ui-host.config';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, AngularMaterialModule, CommonModule], // Add CommonModule
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})

export class LoginComponent implements OnInit {
  private readonly tokenService = inject(TokenService);

  loading = false;
  loadingBranding = false;
  showPassword = false;
  loginUi: LoginUiConfig = this.resolveLoginUiConfig();
  horizontalPosition: MatSnackBarHorizontalPosition = 'end';
  verticalPosition: MatSnackBarVerticalPosition = 'top';

  readonly loginForm = this.fb.nonNullable.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(8)]],
  });

  constructor(
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly snackBar: MatSnackBar,
    private readonly accountTheme: AccountThemeService,
  ) {}

  ngOnInit(): void {
    this.loadBrandingFromApi();
  }

  private resolveLoginUiConfig(): LoginUiConfig {
    return resolveLoginUiFromHost();
  }

  get loginPageStyles(): Record<string, string> {
    return {
      '--login-gradient-start': this.loginUi.gradientStart,
      '--login-gradient-end': this.loginUi.gradientEnd,
      '--logo-bg-start': this.loginUi.logoBgStart,
      '--logo-bg-mid': this.loginUi.logoBgMid,
      '--logo-bg-end': this.loginUi.logoBgEnd,
      '--login-title-color': this.loginUi.titleColor,
      '--login-subtitle-color': this.loginUi.subtitleColor,
      '--login-btn-bg': this.loginUi.btnBgColor || '#186391',
      '--login-btn-hover-bg': this.loginUi.btnHoverBgColor || '#124a6b',
    };
  }

  private loadBrandingFromApi(): void {
    const host = getHostName();
    if (!host) {
      return;
    }

    this.loadingBranding = true;
    this.authService
      .getLoginBranding({ domain_url: host })
      .pipe(finalize(() => (this.loadingBranding = false)))
      .subscribe({
        next: (res) => {
          const payload = this.extractBrandingPayload(res);
          if (!payload) {
            return;
          }
          this.loginUi = this.mergeLoginUi(payload);
        },
        error: () => {
          // Keep local fallback UI when branding API is not reachable.
        },
      });
  }

  private extractBrandingPayload(res: unknown): LoginBrandingConfig | null {
    if (!res || typeof res !== 'object') {
      return null;
    }
    const response = res as Record<string, unknown>;
    const data = response['data'];
    if (data && typeof data === 'object') {
      return data as LoginBrandingConfig;
    }
    return response as LoginBrandingConfig;
  }

  private mergeLoginUi(apiBranding: LoginBrandingConfig): LoginUiConfig {
    const base = this.loginUi ?? DEFAULT_LOGIN_UI;
    return {
      brandName: apiBranding.brandName?.trim() || base.brandName,
      welcomeTitle: apiBranding.welcomeTitle?.trim() || base.welcomeTitle,
      logoPath: apiBranding.logoPath?.trim() || base.logoPath,
      pageClass: apiBranding.pageClass?.trim() || base.pageClass,
      gradientStart: apiBranding.gradientStart?.trim() || base.gradientStart,
      gradientEnd: apiBranding.gradientEnd?.trim() || base.gradientEnd,
      logoBgStart: apiBranding.logoBgStart?.trim() || base.logoBgStart,
      logoBgMid: apiBranding.logoBgMid?.trim() || base.logoBgMid,
      logoBgEnd: apiBranding.logoBgEnd?.trim() || base.logoBgEnd,
      titleColor: apiBranding.titleColor?.trim() || base.titleColor,
      subtitleColor: apiBranding.subtitleColor?.trim() || base.subtitleColor,
      btnBgColor: base.btnBgColor,
      btnHoverBgColor: base.btnHoverBgColor,
    };
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.snackBar.open('Please fill in the required fields', '', { duration: 3000 });
      return;
    }

    this.loading = true;

    const { username, password } = this.loginForm.getRawValue();

    const payload: LoginPayload = {
      username,
      password,
      user_token: null,
    };

    this.authService
      .login(payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: LoginResponse) => this.handleLoginSuccess(res),
        error: (err: unknown) => {
          console.error(err);
          this.snackBar.open('Invalid credentials', '', { duration: 3000 });
        },
      });
  }

  private handleLoginSuccess(res: LoginResponse): void {
    if (!res?.success) {
      this.snackBar.open(res?.message || 'Wrong username or password', '', { duration: 3000 });
      return;
    }

    const userData = res.data;

    if (!userData || !userData.user_id) {
      this.snackBar.open('Invalid user data received', '', { duration: 3000 });
      return;
    }

    this.persistUserSession(userData);
    this.persistAuthTokensFromResponse(res, userData);
    this.accountTheme.applyFromSession();
    this.snackBar.open('Login successful', '', {
      duration: 3000,
      horizontalPosition: this.horizontalPosition,
      verticalPosition: this.verticalPosition,
    });
    const roleId = sessionStorage.getItem('role_id');
    this.router.navigate([getDefaultLandingRoute(roleId)]);
  }

  /**
   * Stores access (and optional refresh) token for the global HTTP auth interceptor.
   * Backend error "Authorization token missing" occurs when this is not set after login.
   */
  private persistAuthTokensFromResponse(res: LoginResponse, userData: UserData): void {
    const root = res as LoginResponse & Record<string, unknown>;
    const fromUser =
      this.pickTokenString(userData.access_token) ??
      this.pickTokenString(userData.token) ??
      this.pickTokenString(userData.auth_token);
    const data = res.data as Record<string, unknown> | undefined;
    const nestedUser = data?.['user'] as Record<string, unknown> | undefined;
    const fromNestedUser =
      nestedUser &&
      (this.pickTokenString(nestedUser['access_token']) ??
        this.pickTokenString(nestedUser['token']) ??
        this.pickTokenString(nestedUser['auth_token']));
    const fromData =
      data &&
      (this.pickTokenString(data['access_token']) ??
        this.pickTokenString(data['token']) ??
        this.pickTokenString(data['auth_token']) ??
        this.pickTokenString(data['accessToken']));
    const fromRoot =
      this.pickTokenString(root['access_token']) ??
      this.pickTokenString(root['token']) ??
      this.pickTokenString(root['auth_token']);
    const access = fromUser ?? fromNestedUser ?? fromData ?? fromRoot;
    if (access) {
      this.tokenService.setAccessToken(this.stripBearerPrefix(access));
    }
    const refreshRaw =
      this.pickTokenString(userData.refresh_token) ??
      (data && this.pickTokenString(data['refresh_token'])) ??
      (nestedUser && this.pickTokenString(nestedUser['refresh_token'])) ??
      this.pickTokenString(root['refresh_token']);
    if (refreshRaw) {
      this.tokenService.setRefreshToken(this.stripBearerPrefix(refreshRaw));
    }
  }

  private pickTokenString(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
  }

  private stripBearerPrefix(token: string): string {
    const t = token.trim();
    if (t.toLowerCase().startsWith('bearer ')) {
      return t.slice(7).trim();
    }
    return t;
  }

  private persistUserSession(userData: UserData): void {
    sessionStorage.setItem('session_id', String(userData.user_id));
    sessionStorage.setItem('role_id', userData.role_id ?? '');
    sessionStorage.setItem('username', userData.username ?? '');
    sessionStorage.setItem('role_name', userData.role_name ?? '');
    sessionStorage.setItem('email_id', userData.email_id ?? '');
    sessionStorage.setItem(
      'full_name',
      `${userData.first_name ?? ''} ${userData.last_name ?? ''}`.trim(),
    );
    sessionStorage.setItem('profile_pic', userData.profile_pic ?? '');

    const accounts = userData.accounts ?? [];

    if (!Array.isArray(accounts) || accounts.length === 0) {
      return;
    }

    sessionStorage.setItem('accounts', JSON.stringify(accounts));

    const approvedAccount =
      accounts.find((a) => a.is_approved === 1) ?? accounts[0];

    if (!approvedAccount) {
      return;
    }

    sessionStorage.setItem('account_id', String(approvedAccount.account_id));
    sessionStorage.setItem('account_name', approvedAccount.account_name ?? '');
    sessionStorage.setItem('block_name', approvedAccount.block_name ?? '');
    sessionStorage.setItem('floor_unit', approvedAccount.floor_unit ?? '');

    if (approvedAccount.floor_unit_id != null) {
      sessionStorage.setItem('floor_unit_id', String(approvedAccount.floor_unit_id));
    }
  }

}